import React from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Custom fallback. If provided, replaces the default UI entirely. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
  /** Optional callback fired when an error is caught. */
  onError?: (error: Error, info: React.ErrorInfo) => void;
  /** Small variant renders a compact tile — use for per-section boundaries. */
  variant?: "page" | "section";
  /** Optional label used in the section-variant fallback. */
  label?: string;
}

interface State {
  error: Error | null;
  info: React.ErrorInfo | null;
}

/**
 * App-wide error boundary. Any render/lifecycle throw below this component
 * is caught here so the app never unmounts to an empty #root ("white page
 * until refresh"). Logs the failure with a `[ErrorBoundary]` tag so the
 * captured console log stream names the actual throwing component + stack.
 *
 * Two variants:
 *   - `page` (default): full card with Reload + Go home. Use around <Outlet />
 *     inside AppShellLayout so the TopBar stays interactive above the fallback.
 *   - `section`: compact tile that says "This section couldn't load". Use to
 *     wrap individual dashboard panels so one bad panel can't take a page down.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Tagged so it's grep-able in the captured console stream.
    // eslint-disable-next-line no-console
    console.error(
      "[ErrorBoundary] render crash",
      {
        message: error?.message,
        stack: error?.stack,
        componentStack: info?.componentStack,
        variant: this.props.variant ?? "page",
        label: this.props.label ?? null,
        pathname: typeof window !== "undefined" ? window.location.pathname : null,
      },
    );
    this.setState({ info });
    try {
      this.props.onError?.(error, info);
    } catch {
      /* swallow — never let onError itself take the app down */
    }
  }

  private reset = () => {
    this.setState({ error: null, info: null });
  };

  private reload = () => {
    try { window.location.reload(); } catch { /* no-op */ }
  };

  private goHome = () => {
    try { window.location.assign("/"); } catch { /* no-op */ }
  };

  render() {
    const { error } = this.state;
    const { children, fallback, variant = "page", label } = this.props;

    if (!error) return <>{children}</>;

    if (fallback) return <>{fallback(error, this.reset)}</>;

    if (variant === "section") {
      return (
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <div className="flex items-start gap-3">
            <RefreshCw className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">
                {label ? `${label} needs a refresh` : "This section needs a refresh"}
              </p>
              <button
                type="button"
                onClick={this.reset}
                className="mt-2 text-xs font-medium text-brand-teal-deep hover:underline"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Page variant — calm, action-oriented. No "error" or "something went
    // wrong" language. TopBar above stays interactive because this boundary
    // sits inside AppShellLayout, below <TopBarV2 />.
    return (
      <div className="flex items-center justify-center py-16 px-4">
        <div className="w-full max-w-md rounded-2xl border bg-card shadow-lg p-6 flex flex-col items-center text-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-muted/40 border border-border flex items-center justify-center">
            <RefreshCw className="w-7 h-7 text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">This page needs a refresh</h2>
            <p className="text-sm text-muted-foreground mt-1">
              A quick refresh should get you moving again. Your navigation up top still works.
            </p>
          </div>
          <div className="w-full flex flex-col gap-2 pt-1">
            <Button
              onClick={this.reload}
              className="w-full h-10 text-sm font-semibold"
              variant="default"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" />
              Refresh
            </Button>
            <button
              type="button"
              onClick={this.goHome}
              className="text-xs text-muted-foreground hover:text-foreground hover:underline"
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
